import type { AdminUsageDashboardSummary, AdminUsageModelFilterOption } from '@nadoutong/contracts';

/**
 * The two readers that let ONE bundle render `/v1/usage/admin-dashboard`
 * correctly against both the payload a platform-api built before
 * router#1681 (#1667, #1672) sends and the payload it sends after.
 *
 * This is not a workaround for a mistake — it is the shape this stage
 * requires. The frontend and platform-api deploy on independent
 * schedules, so on every environment there is a window where a bundle
 * that knows the new contract talks to a server that does not (and,
 * briefly, the reverse). The new fields are REQUIRED in the contract, so
 * an older server type-checks here and simply omits them: read straight,
 * the summary renders `undefined` and the model filter sends
 * `model=undefined`.
 *
 * Everything below is derived from the contract's own account of what
 * changed, never from guessing:
 *
 * - `activeSeats` is documented as the `@deprecated` alias of
 *   `seatsWithUsage`, so it is the exact fallback for that one number.
 * - before #1667 `activeApiKeys` WAS the window-scoped key count wearing
 *   a roster name — that mislabelling is the defect #1667 fixed — so on
 *   an old payload it answers "keys with usage" and NOTHING answers
 *   "keys on the roster", which is therefore reported as unknown rather
 *   than filled in with the number next to it.
 * - before #1672 a model filter option carried only `{ id, label }` and
 *   the id WAS the bare model name, which is precisely what the new
 *   query spells as a bare `model` with no `vendor`: the union across
 *   every vendor serving that name.
 *
 * REMOVE THIS FILE once router#1681 is deployed to dev, staging AND
 * production. Removing it means: read `summary.seatsWithUsage`,
 * `summary.keysWithUsage`, `option.model` and `option.vendor` directly at
 * the call sites, and drop `usage.admin.summary.keys_roster_unknown` from
 * both locales.
 */

/**
 * The four seat/key numbers of the summary, each already resolved to the
 * caliber its name states. `seatsWithUsage` and `keysWithUsage` are
 * window-scoped; `seatsOnRoster` and `keysOnRoster` are roster-scoped and
 * window-independent.
 *
 * A window number is NOT a part of its roster number: a member or key
 * that spent tokens inside the window and was deleted or revoked
 * afterwards counts in the window and not on the roster, so
 * `seatsWithUsage` may legitimately exceed `seatsOnRoster`. Nothing here
 * may be rendered as one ratio.
 */
export interface SeatKeyCalibers {
  readonly seatsWithUsage: number;
  readonly seatsOnRoster: number;
  readonly keysWithUsage: number;
  /** `null` when the server predates #1667 and never sent a roster count. */
  readonly keysOnRoster: number | null;
}

export function seatKeyCalibers(summary: AdminUsageDashboardSummary): SeatKeyCalibers {
  // Widened on purpose: the contract types both as required, and the
  // whole point here is the server that does not send them.
  const seatsWithUsage: number | undefined = summary.seatsWithUsage;
  const keysWithUsage: number | undefined = summary.keysWithUsage;
  return {
    seatsWithUsage: seatsWithUsage ?? summary.activeSeats ?? 0,
    seatsOnRoster: summary.totalSeats,
    keysWithUsage: keysWithUsage ?? summary.activeApiKeys,
    keysOnRoster: keysWithUsage === undefined ? null : summary.activeApiKeys,
  };
}

/** The (vendor, model) pair a filter option selects. */
export interface ModelDimension {
  readonly model: string;
  readonly vendor: string;
}

/**
 * The structured dimension behind a model filter option.
 *
 * The option's `id` is never parsed — the contract forbids it in so many
 * words, and `vendor` / `model` carry the same information without a
 * separator to get wrong. `id` is only read when the fields are absent,
 * where it is not a composite at all but the bare model name an
 * older server sent.
 */
export function modelOptionDimension(option: AdminUsageModelFilterOption): ModelDimension {
  const model: string | undefined = option.model;
  const vendor: string | undefined = option.vendor;
  return { model: model ?? option.id, vendor: vendor ?? '' };
}

/** Whether an option selects exactly this dimension. */
export function isModelOptionFor(
  option: AdminUsageModelFilterOption,
  dimension: ModelDimension,
): boolean {
  const actual = modelOptionDimension(option);
  return actual.model === dimension.model && actual.vendor === dimension.vendor;
}
